/* i18n/index.js — THE TRANSLATION ENGINE (one engine, whole platform).
 *
 * Contract
 * --------
 *   t("catalog.registry.title")                  → the string for the active language
 *   t("catalog.registry.count", { n: 12 })       → interpolation of {n}
 *   t("common.item", { count })                  → picks .one / .other automatically
 *   fmt.money(1234.5)                            → locale + tenant currency
 *   fmt.number(3.14159, { maximumFractionDigits: 2 })
 *   fmt.date(iso), fmt.dateTime(iso)
 *
 * Principles
 *   · NOTHING is hardcoded: languages come from the registry (i18n/locales),
 *     message catalogues are data, and the currency comes from tenant settings.
 *   · A missing key NEVER renders blank — it falls back to English, then to a
 *     humanised form of the key itself, and is recorded so the migration can
 *     be driven by evidence (window.__I18N_MISSING in dev).
 *   · RTL is a property of the language, applied once to <html dir>; no
 *     component ever needs to know about it.
 *   · Zero dependencies — the platform controls its own formatting and bundle.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { DEFAULT_LOCALE, getLocale, isRtl, listLocales, configureLocales } from "./locales";
import en from "./messages/en.json";
import sq from "./messages/sq.json";
import it from "./messages/it.json";
import de from "./messages/de.json";
import es from "./messages/es.json";
import fr from "./messages/fr.json";
import ar from "./messages/ar.json";

/* Bundled catalogues (2026-08-05: all 7 shipped languages carry the platform
 * chrome — auth/nav/store/studio/crm/roles; sq additionally carries the full
 * technician). A language with no catalogue (or a missing key) still works —
 * it simply falls back to English until its file arrives. */
const CATALOGUES = { en, sq, it, de, es, fr, ar };
/** Merge a catalogue at runtime (server-delivered or SuperAdmin-authored). */
export function registerMessages(code, messages) {
  const c = String(code || "").toLowerCase();
  CATALOGUES[c] = { ...(CATALOGUES[c] || {}), ...(messages || {}) };
}

const STORE_KEY = "lhc-locale";
const MISSING = new Set();

function lookup(cat, key) {
  if (!cat) return undefined;
  if (cat[key] !== undefined) return cat[key];      // flat keys win (authoring convenience)
  return key.split(".").reduce((o, part) => (o && typeof o === "object" ? o[part] : undefined), cat);
}
/** "catalog.registry.title" → "Title" — a readable last resort, never blank. */
function humanise(key) {
  const last = String(key).split(".").pop() || "";
  const spaced = last.replace(/[_-]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
function interpolate(str, vars) {
  if (!vars || typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] === undefined || vars[k] === null ? m : String(vars[k])));
}

/** translate(locale, key, vars) — the pure core, usable outside React. */
export function translate(locale, key, vars) {
  if (!key) return "";
  let val = lookup(CATALOGUES[locale], key);
  if (val === undefined && locale !== DEFAULT_LOCALE) val = lookup(CATALOGUES[DEFAULT_LOCALE], key);
  // plural forms: { one, other } chosen by vars.count
  if (val && typeof val === "object" && !Array.isArray(val)) {
    const n = vars && typeof vars.count === "number" ? vars.count : null;
    val = n === null ? (val.other || val.one) : (n === 1 ? (val.one || val.other) : (val.other || val.one));
  }
  if (val === undefined) {
    if (!MISSING.has(key)) {
      MISSING.add(key);
      if (typeof window !== "undefined") {
        window.__I18N_MISSING = Array.from(MISSING);
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.warn("[i18n] missing key:", key, "(" + locale + ")");
        }
      }
    }
    return interpolate(humanise(key), vars);
  }
  return interpolate(val, vars);
}
export function missingKeys() { return Array.from(MISSING); }

/* ── formatters — Intl bound to the active locale + tenant currency ─────── */
export function makeFormatters(locale, currency) {
  const cur = currency || "EUR";
  const nf = (opts) => new Intl.NumberFormat(locale || DEFAULT_LOCALE, opts);
  return {
    number: (n, opts) => nf(opts).format(Number(n) || 0),
    money: (n, opts) => nf({ style: "currency", currency: cur, minimumFractionDigits: 2, maximumFractionDigits: 2, ...(opts || {}) }).format(Number(n) || 0),
    money0: (n) => nf({ style: "currency", currency: cur, maximumFractionDigits: 0 }).format(Number(n) || 0),
    percent: (n) => nf({ style: "percent", maximumFractionDigits: 1 }).format(Number(n) || 0),
    date: (v) => (v ? new Intl.DateTimeFormat(locale || DEFAULT_LOCALE, { dateStyle: "medium" }).format(new Date(v)) : ""),
    dateTime: (v) => (v ? new Intl.DateTimeFormat(locale || DEFAULT_LOCALE, { dateStyle: "medium", timeStyle: "short" }).format(new Date(v)) : ""),
    currency: cur,
  };
}

const Ctx = createContext(null);

function initialLocale() {
  try {
    const saved = typeof localStorage !== "undefined" && localStorage.getItem(STORE_KEY);
    if (saved && getLocale(saved)) return saved;
  } catch (e) { /* private mode */ }
  try {
    const nav = (typeof navigator !== "undefined" && (navigator.language || "")).slice(0, 2).toLowerCase();
    if (nav && getLocale(nav)) return nav;
  } catch (e) { /* non-browser */ }
  return DEFAULT_LOCALE;
}

/** Wrap the app once. Everything below can translate and format. */
export function I18nProvider({ children, currency }) {
  const [locale, setLocaleState] = useState(initialLocale);

  // RTL + lang are document-level facts, applied once per change — no
  // component ever branches on direction.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const el = document.documentElement;
    el.setAttribute("lang", locale);
    el.setAttribute("dir", isRtl(locale) ? "rtl" : "ltr");
  }, [locale]);

  const setLocale = useCallback((code) => {
    if (!getLocale(code)) return;
    setLocaleState(code);
    try { localStorage.setItem(STORE_KEY, code); } catch (e) { /* ignore */ }
  }, []);

  const value = useMemo(() => ({
    locale,
    setLocale,
    dir: isRtl(locale) ? "rtl" : "ltr",
    locales: listLocales(),
    t: (key, vars) => translate(locale, key, vars),
    fmt: makeFormatters(locale, currency),
  }), [locale, setLocale, currency]);

  return React.createElement(Ctx.Provider, { value }, children);
}

/** The hook every component uses. Safe outside a provider (English + EUR), so
 *  partially-migrated screens and unit tests never crash. */
export function useI18n() {
  const ctx = useContext(Ctx);
  return ctx || {
    locale: DEFAULT_LOCALE,
    setLocale: () => {},
    dir: "ltr",
    locales: listLocales(),
    t: (key, vars) => translate(DEFAULT_LOCALE, key, vars),
    fmt: makeFormatters(DEFAULT_LOCALE),
  };
}
/** Convenience for the common case. */
export function useT() { return useI18n().t; }

export { listLocales, configureLocales, getLocale, isRtl, DEFAULT_LOCALE };
